import {
  CheckCircle,
  ArrowRight,
  Users,
  TrendingUp,
  Target,
  Lightbulb,
  MessageSquare,
  Heart,
  Brain,
  Trophy,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";

const Services = () => {
  const pillars = [
    {
      title: "EMPOWER",
      icon: Users,
      description:
        "We provide individuals with the tools, knowledge, and confidence necessary to take ownership of their professional development through:",
      features: [
        "Self-awareness and emotional intelligence building",
        "Critical thinking and problem-solving development",
        "Innovation and creative thinking enhancement",
        "Advanced decision-making capabilities",
        "Continuous learning mindset cultivation",
      ],
    },
    {
      title: "ELEVATE",
      icon: TrendingUp,
      description:
        "We raise performance standards across all organizational levels through:",
      features: [
        "Competency-based training frameworks",
        "Targeted skills gap interventions",
        "Performance measurement strategies",
        "Multi-level leadership development",
        "Organizational culture transformation",
      ],
    },
    {
      title: "EXCEL",
      icon: Trophy,
      description:
        "We deliver measurable improvements in productivity and satisfaction by:",
      features: [
        "Implementing industry best practices",
        "Developing high-performance teams",
        "Creating sustainable systems",
        "Fostering innovation cultures",
        "Building resilient organizations",
      ],
    },
  ];

  const serviceCategories = [
    {
      title: "Leadership Development",
      icon: Target,
      programs: [
        {
          name: "Strategic Leadership",
          duration: "5-day intensive programs for senior executives",
          description:
            "Advanced leadership capabilities for C-suite leaders and department heads",
        },
        {
          name: "Emerging Leaders",
          duration: "3-day programs for middle management",
          description:
            "Leadership transition, team building, and performance management",
        },
        {
          name: "Women in Leadership",
          duration: "Specialized 4-day empowerment programs",
          description:
            "Barrier overcome, confidence building, and networking mastery",
        },
      ],
    },
    {
      title: "Project Management Excellence",
      icon: CheckCircle,
      programs: [
        {
          name: "PMP Preparation",
          duration: "6-day certification preparation",
          description:
            "Comprehensive preparation for Project Management Professional certification",
        },
        {
          name: "Agile Management",
          duration: "3-day modern methodology training",
          description: "Modern project management methodologies and frameworks",
        },
        {
          name: "Monitoring & Evaluation",
          duration: "4-day impact measurement programs",
          description: "Results-based monitoring and evaluation systems",
        },
      ],
    },
    {
      title: "Management Excellence",
      icon: TrendingUp,
      programs: [
        {
          name: "Performance Management Systems",
          duration: "3-day framework development",
          description: "Building effective performance management frameworks",
        },
        {
          name: "Financial Management for Non-Financial Managers",
          duration: "4-day financial literacy",
          description:
            "Essential financial skills for non-financial professionals",
        },
        {
          name: "Operations Management",
          duration: "5-day process improvement training",
          description: "Process optimization and operational excellence",
        },
        {
          name: "Strategic Management",
          duration: "Comprehensive strategic planning programs",
          description: "Strategic thinking, planning, and implementation",
        },
      ],
    },
    {
      title: "Professional Development",
      icon: Lightbulb,
      programs: [
        {
          name: "Career Growth Excellence",
          duration: "2-day career advancement programs",
          description: "Strategic career development and advancement planning",
        },
        {
          name: "Time Management & Productivity",
          duration: "2-day efficiency enhancement",
          description: "Personal effectiveness and productivity optimization",
        },
        {
          name: "Professional Conduct",
          duration: "1-day workplace etiquette training",
          description: "Professional behavior and workplace ethics",
        },
      ],
    },
    {
      title: "Communication Excellence",
      icon: MessageSquare,
      programs: [
        {
          name: "Business Communication",
          duration: "3-day comprehensive communication skills",
          description:
            "Written, verbal, and presentation communication mastery",
        },
        {
          name: "Public Speaking",
          duration: "2-day presentation mastery",
          description: "Confident public speaking and presentation skills",
        },
        {
          name: "Conflict Resolution",
          duration: "3-day negotiation and mediation training",
          description:
            "Effective conflict management and resolution techniques",
        },
      ],
    },
    {
      title: "Customer Excellence",
      icon: Heart,
      programs: [
        {
          name: "Service Excellence",
          duration: "2-day customer service mastery",
          description: "Exceptional customer service delivery and management",
        },
        {
          name: "CRM Systems",
          duration: "3-day relationship management training",
          description: "Customer relationship management best practices",
        },
      ],
    },
    {
      title: "Wellness & Integration",
      icon: Heart,
      programs: [
        {
          name: "Stress Management",
          duration: "2-day resilience building",
          description: "Building personal resilience and stress management",
        },
        {
          name: "Mental Health Awareness",
          duration: "1-day workplace wellness training",
          description: "Workplace mental health awareness and support",
        },
      ],
    },
    {
      title: "Life Skills",
      icon: Brain,
      programs: [
        {
          name: "Personal Development",
          duration: "Comprehensive life skills programs",
          description:
            "Essential life skills for personal and professional success",
        },
      ],
    },
    {
      title: "Performance & Career Coaching",
      icon: Trophy,
      programs: [
        {
          name: "Executive Coaching",
          duration: "Personalized coaching programs",
          description: "One-on-one leadership and performance coaching",
        },
        {
          name: "Career Transition Support",
          duration: "Customized career coaching",
          description: "Strategic career planning and transition support",
        },
      ],
    },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section
        className="relative py-16 lg:py-22 bg-white overflow-hidden"
        id="services"
      >
        {/* Background Animation Image */}
        <div className="absolute inset-0 z-0 opacity-50 pointer-events-none">
          <img
            src="/services.svg"
            alt="Animated background"
            className="absolute top-0 left-0 w-40 sm:w-60 md:w-80 lg:w-[400px] animate-float-slow"
          />

          {/* Bottom-Right Blob */}
          <img
            src="/blob.svg"
            alt="Animated blob"
            className="absolute bottom-0 right-0 w-36 sm:w-55 md:w-65 lg:w-[350px] animate-pulse"
          />
        </div>

        {/* Hero Content */}
        <div className="container relative z-10 mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h1 className="text-4xl lg:text-6xl font-bold text-foreground">
              Our <span className="text-primary">Services</span>
            </h1>
            <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed">
              Comprehensive training solutions designed to{" "}
              <strong>Empower</strong>, <strong>Elevate</strong>, and{" "}
              <strong>Excel</strong> your organization's performance across all
              levels.
            </p>
          </div>
        </div>
      </section>

      {/* Three-Pillar Approach */}
      <section
        className="py-12 lg:py-16 bg-white"
        // style={{ backgroundImage: "url('/images/pillar-bg.jpg')" }}
      >
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-2xl lg:text-2xl font-semibold text-foreground">
              Our Three-Pillar Approach
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Every service we offer is built on our proven three-pillar
              methodology.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {pillars.map((pillar, index) => (
              <Card
                key={index}
                className={`h-full shadow-subtle hover:shadow-elegant transition-smooth ${
                  index % 2 === 0 ? "bg-blue-50" : "bg-white"
                }`}
              >
                <CardContent className="p-8 space-y-6">
                  <div className="text-center space-y-4">
                    <div className="w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center mx-auto">
                      <pillar.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-primary">
                      {pillar.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {pillar.description}
                    </p>
                  </div>

                  <ul className="space-y-3">
                    {pillar.features.map((feature, featureIndex) => (
                      <li
                        key={featureIndex}
                        className="flex items-start space-x-3"
                      >
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-muted-foreground">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Categories */}
      <section className="py-12 lg:py-16 bg-gradient-secondary">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold text-foreground">
              Service Categories
            </h2>
            <p className="text-lg md:text-md text-muted-foreground max-w-2xl mx-auto">
              Explore our comprehensive range of training programs organized by
              focus area.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {serviceCategories.map((category, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white rounded-lg shadow-subtle"
                >
                  <AccordionTrigger className="px-6 py-4 hover:no-underline">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
                        <category.icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-sm md:text-base font-semibold md:font-medium text-foreground">
                        {category.title}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {category.programs.map((program, programIndex) => (
                        <Card
                          key={programIndex}
                          className="border border-border"
                        >
                          <CardContent className="p-4 space-y-3">
                            <h4 className="font-semibold text-foreground">
                              {program.name}
                            </h4>
                            <p className="text-sm text-primary font-medium">
                              {program.duration}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {program.description}
                            </p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 lg:py-16 bg-gradient-primary text-white">
        <div className="container mx-auto px-4 text-center space-y-8">
          <div className="space-y-4">
            <h2 className="text-3xl lg:text-4xl font-bold">
              We are here to help you Build Your Success Blueprint
            </h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              From strategy to execution, we design training solutions tailored
              to your organization’s goals and industry needs.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="secondary" size="lg" asChild>
              <Link to="/contact">
                Get Custom Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="border-white text-black hover:bg-white hover:text-primary"
              asChild
            >
              <Link to="/programs">View Training Programs</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Services;
